/**
 * L'APERÇU CAISSE DU STUDIO — la disposition rendue dans le cadre d'une tablette (paysage 4:3, 1024 × 768) ou d'un
 * téléphone (« pocket », portrait, 393 × 852), à la taille LOGIQUE de l'appareil puis réduite d'un bloc : ce qu'on voit
 * ici a les proportions de ce que le caissier touchera. Sur le téléphone, le menu fixe se réduit à ses pictos et le
 * ticket passe en barre du bas, comme dans la caisse (maquette `pvDims`).
 *
 * ⚠️ LES TUILES RÉAGISSENT, ET RIEN NE S'ÉCRIT : un dossier s'ouvre, un article s'ajoute à un ticket SIMULÉ, avec le
 *    prix que la caisse appliquera. Aucune vente, aucun appel au serveur — et le ticket le dit.
 * ⚠️ L'APERÇU APPLIQUE LES RÉGLAGES DE LA DISPOSITION (colonnes, hauteur ajustée, menu fixe, retour à l'accueil) :
 *    ils commandent la caisse, donc ne pas les montrer ici ferait prévisualiser un autre écran.
 * ⚠️ CE QUE LA CAISSE DESSINE, L'APERÇU LE DESSINE, PAR LA MÊME RÈGLE (`drawnTiles`/`drawnCount`/`drawnSourceLevel`, au
 *    contrat) : un article qu'elle ne vend pas n'apparaît pas, un dossier annonce ce qu'on peut y toucher, et le niveau
 *    d'une source se range, se masque et se borne comme en caisse — la borne DITE, jamais une coupe muette.
 * ⚠️ UNE SOURCE DONT LE CONTENU N'EST PAS ENCORE LU NE MONTRE PAS SES SEULES TUILES POSÉES : la caisse a ce contenu sur
 *    sa base, un niveau à moitié dessiné prévisualiserait un écran qu'elle n'affiche jamais. On dit qu'on lit. Les mesures de la tuile sont les
 *    siennes aussi (`QUICK_TILE`, `@orgazone/ui-tokens`) — écrites deux fois, elles divergeraient au premier réglage.
 * ⚠️ UNE TUILE SE REND COMME LA TABLETTE LA DESSINE (maquette : « ce que la tablette montre, l'aperçu le montre ») —
 *    dans sa version (texte seul, petit picto, grand picto, photo ; une tuile sans visuel est en « Petit picto »),
 *    à la hauteur de son contenu, chaque rangée à celle de sa tuile la plus haute : les fonctions de la charte que la
 *    caisse lit (`quick-studio-tile`). Sa photo est celle que la caisse choisira par la règle du contrat (`studioFace`) :
 *    la photo du comptoir, puis celle du studio (cadrée), puis le visuel de la fiche. Seule manque une photo prise au
 *    comptoir et PAS ENCORE remontée : elle n'existe que sur la tablette qui l'a prise.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Icon, rovingRadio, type IconName } from '@orgazone/ui-web';
import { QUICK_RAIL, QUICK_RAIL_POCKET, SURFACE_DEFAULTS, quickTileVariant } from '@orgazone/ui-tokens';
import {
  QUICK_POUR_MAX, drawnCount, drawnSourceLevel, drawnTiles, isPresetChildId, isSourceKind, opensLevel, railButtons, tileKey,
  type QuickLevelEntry, type QuickScreenVisuals, type QuickSourceKind, type QuickTileLook,
} from '@orgazone/sync-contract';
import type { StudioArticle, StudioLayout, StudioSource, StudioTile } from '../api';
import { t } from '../i18n';
import { codeCaisse } from '../caisse';
import { RailButton, RailPictoButton, railStyle, studioFace, tileTone } from './quick-studio-look';
import {
  deepTileTotal, descendantIds, formatPrice, isSourceTile, levelTiles, sourceRefOf, tileById, tileName, type PourState,
  type PresetValuesState,
} from './quick-studio-model';
import { StudioTileBody, articleContent, studioGrid, studioTileFrame, useGridWidth, type StudioTileContent } from './quick-studio-tile';

/**
 * LES APPAREILS DE L'APERÇU, en pixels logiques — le cadre se réduit, il ne grossit jamais. Marge de la grille, largeur
 * et coins du cadre, coins de l'écran : ceux de la maquette.
 */
const DEVICES = {
  tablet: { width: 1024, height: 768, gridPad: 14, frameMax: 1040, frameRadius: 28, screenRadius: 14 },
  pocket: { width: 393, height: 852, gridPad: 10, frameMax: 429, frameRadius: 44, screenRadius: 30 },
} as const;
type Device = keyof typeof DEVICES;
/** Le ticket simulé à droite de la grille de la tablette — le téléphone le passe en barre du bas. */
const TICKET_W = 258;

/** Les deux appareils de l'aperçu, sous leurs mots et leurs pictos (maquette `pvDevs`). */
const DEVICE_CHOICES: { id: Device; label: () => string; icon: IconName }[] = [
  { id: 'tablet', label: () => t('Tablette'), icon: 'tabletWide' },
  { id: 'pocket', label: () => t('Pocket'), icon: 'smartphone' },
];

interface CartLine { articleId: string; name: string; priceCents: number | null; qty: number }

export function QuickStudioPreview({ layout, layoutName, articles, sources, pours, presetValues, onNeedPours, onRetryPour, startPath }: {
  layout: StudioLayout;
  layoutName: string;
  articles: Record<string, StudioArticle>;
  sources: Record<string, StudioSource>;
  pours: Record<string, PourState>;
  /** Les valeurs que les préréglages déploient — un préréglage pas encore lu ne s'annonce pas « vide ». */
  presetValues: Partial<Record<QuickSourceKind, PresetValuesState>>;
  /** Demande le contenu de ces sources (`sourceRef`) — le conteneur le lit une fois et le garde. */
  onNeedPours: (refs: string[]) => void;
  onRetryPour: (ref: string) => void;
  /** Le niveau que l'on compose : l'aperçu s'y ouvre, tant qu'on n'a pas navigué dans l'aperçu lui-même. */
  startPath: string[];
}) {
  const wrap = useRef<HTMLDivElement | null>(null);
  const [device, setDevice] = useState<Device>('tablet');
  const D = DEVICES[device];
  const pocket = device === 'pocket';
  const [width, setWidth] = useState<number>(DEVICES.tablet.width);
  const [navPath, setNavPath] = useState<string[] | null>(null);
  const [cart, setCart] = useState<CartLine[]>([]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const measure = (): void => setWidth(Math.round(el.getBoundingClientRect().width) || DEVICES.tablet.width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rail = layout.settings.rail;
  // Rail actif : l'accueil est le rail, le haut de la grille est le bouton de rail ouvert.
  const top = rail ? 1 : 0;
  const path = navPath ?? startPath;
  const level = path.length > 0 ? path[path.length - 1]! : null;
  // La caisse ne dessine que les articles actifs qu'elle a reçus — l'aperçu non plus.
  const sellable = (articleId: string): boolean => articles[articleId]?.active === true;
  const levelTile = level === null ? undefined : layout.tiles.find((x) => x.id === level);
  const levelRef = levelTile ? sourceRefOf(levelTile) : null;
  const levelPour = levelRef ? pours[levelRef] : undefined;
  // ⚠️ Ce qu'une source déverse est vendable PAR CONSTRUCTION (le serveur ne déverse que du vendable), détail envoyé ou
  //    non : au-delà des premiers, l'aperçu le dessine quand même, comme la caisse.
  const poured = new Set(levelPour?.status === 'ready' ? levelPour.ids : []);
  const sellableHere = (articleId: string): boolean => poured.has(articleId) || sellable(articleId);
  let cells: QuickLevelEntry<StudioTile>[] = [];
  let beyond = 0;
  if (!levelTile || !levelRef) {
    cells = drawnTiles(levelTiles(layout, level), sellable).map((tile) => ({ key: tileKey(tile.id), tile, articleId: null }));
  } else if (levelPour?.status === 'ready') {
    const drawn = drawnSourceLevel(levelTiles(layout, level), levelPour.ids, levelTile.hidden, levelTile.order, sellableHere);
    cells = drawn.entries;
    beyond = drawn.beyond;
  }
  const scale = Math.min(1, width / D.width);
  const nameOf = (tile: StudioTile): string => tileName(tile, articles, sources);
  const names = path.map((id) => layout.tiles.find((x) => x.id === id)).filter((x): x is StudioTile => !!x).map(nameOf);
  /** Les articles qu'ouvre une tuile, tous niveaux confondus (`null` : en lecture) — ce que la caisse annonce. */
  const countOf = (tile: StudioTile): number | null => deepTileTotal(layout, tile, pours, articles, sources, presetValues);

  // Le contenu des sources se lit quand l'aperçu en a besoin : le niveau ouvert, ses tuiles, le rail.
  const wanted = useMemo(() => {
    const refs = new Set<string>();
    const want = (tile: StudioTile | undefined): void => { const r = tile ? sourceRefOf(tile) : null; if (r) refs.add(r); };
    want(levelTile);
    // ⚠️ Une tuile déployée par un préréglage ne se lit qu'ouverte : son compte vient du serveur (`sourceTileTotal`).
    // ⚠️ SOUS CHAQUE TUILE AUSSI : sa pastille compte les articles de tous ses niveaux (`deepTileTotal`).
    const deep = (tile: StudioTile): void => {
      if (isPresetChildId(tile.id)) return;
      want(tile);
      for (const id of descendantIds(layout, tile.id)) { const x = tileById(layout, id); if (x && !isPresetChildId(x.id)) want(x); }
    };
    for (const tile of levelTiles(layout, level)) deep(tile);
    if (rail) for (const tile of levelTiles(layout, null)) if (!isPresetChildId(tile.id)) want(tile);
    return [...refs].sort().join('|');
  }, [layout, level, levelTile, rail]);
  // ⚠️ `pours` en dépendance, comme au composeur : un rechargement vide le contenu lu sans changer les sources voulues.
  useEffect(() => { if (wanted) onNeedPours(wanted.split('|')); }, [wanted, onNeedPours, pours]);
  const title = names.length === 0 ? t('Écran d’accueil') : `${names.length > 2 ? '… › ' : ''}${names.slice(-2).join(' › ')}`;

  const addToCart = (articleId: string): void => {
    const a = articles[articleId];
    if (!a) return;
    setCart((prev) => {
      const i = prev.findIndex((line) => line.articleId === a.id);
      if (i < 0) return [...prev, { articleId: a.id, name: a.name, priceCents: a.priceCents, qty: 1 }];
      return prev.map((line, j) => (j === i ? { ...line, qty: line.qty + 1 } : line));
    });
    // « Retour à l'accueil après ajout » : le réglage que la caisse appliquera, appliqué ici aussi.
    if (layout.settings.home && path.length > top) setNavPath(path.slice(0, top));
  };
  const tap = (tile: StudioTile): void => {
    if (tile.kind === 'article') { if (tile.articleId) addToCart(tile.articleId); }
    else if (opensLevel(tile.kind)) setNavPath([...path, tile.id]);
  };
  const total = cart.reduce((sum, line) => sum + (line.priceCents ?? 0) * line.qty, 0);
  const pieces = cart.reduce((sum, line) => sum + line.qty, 0);
  const unpriced = cart.some((line) => line.priceCents == null);
  const railTiles = useMemo(() => (rail ? railButtons(levelTiles(layout, null), (id) => articles[id]?.active === true) : []), [rail, layout, articles]);
  // ⚠️ LES CASES SE CALCULENT DANS LES PIXELS DE LA TABLETTE : la largeur de la grille, mesurée sous la réduction.
  const gridRef = useRef<HTMLDivElement | null>(null);
  //    Chaque voisin (rail, ticket) est séparé de la grille par un pixel.
  const gridWidth = useGridWidth(gridRef, pocket
    ? D.width - (rail ? QUICK_RAIL_POCKET.width + 1 : 0) - 2 * D.gridPad
    : D.width - (rail ? QUICK_RAIL.width + 1 : 0) - (TICKET_W + 1) - 2 * D.gridPad);
  // Dans le niveau d'une source, ses articles prennent l'habillage qu'elle leur donne (« Ses articles »).
  const sourceLook = levelTile && isSourceKind(levelTile.kind) ? levelTile.pour : null;
  const drawn = cells.map((e) => drawnCell(e, articles, sourceLook, layout.settings));
  const grid = studioGrid(drawn.map((d) => d.cell), gridWidth, layout.settings.cols);

  // Les colonnes que l'écran tient — et, si l'on en a imposé davantage, le dire (maquette `pvColsL`).
  const asked = layout.settings.cols;
  const colsLabel = asked !== null && asked > grid.cols
    ? t('{n} colonne de {largeur} px — {demandees}, l’écran n’en tient que {n}', { n: grid.cols, largeur: Math.round(grid.unitW), demandees: t('{n} demandée', { n: asked }) })
    : t('{n} colonne de {largeur} px', { n: grid.cols, largeur: Math.round(grid.unitW) });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* L'APPAREIL PRÉVISUALISÉ : même disposition, autre largeur — la grille recompte ses colonnes (maquette). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', paddingBottom: 12 }}>
        <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-500)' }}>{t('Aperçu sur')}</span>
        {/* ⚠️ LE SÉLECTEUR DE LA MAQUETTE, PAS LE SEGMENTÉ PARTAGÉ : deux pixels de marge, l'appareil choisi à l'encre — celui de
            la charte, plus épais et à la marque, décalait toute la tablette de deux pixels. */}
        <span role="radiogroup" aria-label={t('Appareil de l’aperçu')} style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
          {DEVICE_CHOICES.map((d, i) => {
            const on = device === d.id;
            return (
              <button key={d.id} type="button" role="radio" aria-checked={on} onClick={() => setDevice(d.id)}
                {...rovingRadio({ index: i, count: DEVICE_CHOICES.length, checked: on, anyChecked: true, onPick: (k) => setDevice(DEVICE_CHOICES[k]!.id) })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'transparent', color: on ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <Icon name={d.icon} size={14} stroke={1.9} />{d.label()}
              </button>
            );
          })}
        </span>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{colsLabel}</span>
      </div>
      {/* ⚠️ LE CADRE EST L'APPAREIL LUI-MÊME, NOIR DANS LES DEUX THÈMES : une encre de la charte (`--ink-950`) basculait au
          blanc en sombre, et son étiquette blanche disparaissait dessus. */}
      <div style={{ width: '100%', maxWidth: D.frameMax, background: SURFACE_DEFAULTS.dark.canvas, borderRadius: D.frameRadius, padding: '18px 18px 22px', boxSizing: 'border-box', boxShadow: '0 30px 70px -24px rgba(12,14,16,.55), inset 0 0 0 1.5px rgba(255,255,255,.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 6px 12px' }}>
          <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>
            {/* ⚠️ LA CAISSE QUI AFFICHE CETTE DISPOSITION (maquette : « Caisse R01 · … ») — la première qui lui est affectée.
                Sans caisse affectée, aucune n'est à nommer : l'étiquette dit alors que c'est un aperçu. */}
            {layout.registers[0]
              ? t('Caisse {caisse} · {disposition}', { caisse: codeCaisse(layout.registers[0]), disposition: layoutName })
              : t('Aperçu · {disposition}', { disposition: layoutName })}
          </span>
          <span style={{ flex: 1 }} />
          {/* La caméra de l'appareil (maquette) — un repère de forme, rien à lire. */}
          <span aria-hidden style={{ width: 8, height: 8, flexShrink: 0, borderRadius: 'var(--r-pill)', background: 'rgba(255,255,255,.18)', boxShadow: 'inset 0 0 0 2px rgba(0,0,0,.5)' }} />
          <span style={{ marginLeft: 6, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', color: 'rgba(255,255,255,.4)', whiteSpace: 'nowrap' }}>
            {pocket ? t('pocket · {n} colonne', { n: grid.cols }) : t('tablette 4:3 · {n} colonne', { n: grid.cols })}
          </span>
        </div>
        <div ref={wrap} style={{ width: '100%', height: Math.round(D.height * scale), position: 'relative', overflow: 'hidden', borderRadius: D.screenRadius }}>
          {/* ⚠️ LE FOND DE L'ÉCRAN EST LE FILET : rail, grille et ticket se posent à un pixel l'un de l'autre sur ink-200 (maquette). */}
          <div style={{ width: D.width, height: D.height, transform: `scale(${scale})`, transformOrigin: 'top left', display: 'flex', flexDirection: pocket ? 'column' : 'row', background: 'var(--ink-200)' }}>
            {pocket && (
              // L'EN-TÊTE DU TÉLÉPHONE (la coquille Pocket de la caisse) : l'écran de vente et la disposition qu'il montre.
              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: SURFACE_DEFAULTS.dark.canvas, color: '#fff' }}>
                <span aria-hidden style={{ width: 32, height: 32, borderRadius: 'var(--r-ctrl)', background: 'rgba(255,255,255,.08)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="menu" size={16} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-h4)', fontWeight: 700 }}>{t('Vente')}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'rgba(255,255,255,.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{layoutName}</span>
                </span>
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', gap: 1 }}>
            {rail && pocket && (
              // LE MENU FIXE DU TÉLÉPHONE, réduit à ses pictos (`QUICK_RAIL_POCKET`) — le nom en infobulle.
              <div style={{ flex: `0 0 ${QUICK_RAIL_POCKET.width}px`, minWidth: 0, borderRight: '1px solid var(--ink-200)', background: 'var(--ink-50)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: QUICK_RAIL_POCKET.gap, padding: `${QUICK_RAIL_POCKET.padY}px 0`, overflowY: 'auto' }}>
                {railTiles.map((tile) => {
                  const open = path[0] === tile.id;
                  return (
                    <RailPictoButton key={tile.id} tile={tile} name={nameOf(tile)} open={open} screen={layout.settings}
                      article={tile.kind === 'article' ? articles[tile.articleId ?? ''] : undefined}
                      onClick={() => (tile.kind === 'article' ? addToCart(tile.articleId ?? '') : setNavPath([tile.id]))} />
                  );
                })}
              </div>
            )}
            {rail && !pocket && (
              // ⚠️ Le rail occupe toute la hauteur de l'écran, comme en caisse, et y défile : la borne de 620 px de la
              //    maquette laissait un vide sous le rail que la tablette n'a pas.
              <div style={railStyle(null)}>
                {railTiles.map((tile) => {
                  const open = path[0] === tile.id;
                  return (
                    <RailButton key={tile.id} tile={tile} name={nameOf(tile)} open={open} active={open} screen={layout.settings}
                      article={tile.kind === 'article' ? articles[tile.articleId ?? ''] : undefined}
                      onClick={() => (tile.kind === 'article' ? addToCart(tile.articleId ?? '') : setNavPath([tile.id]))} />
                  );
                })}
              </div>
            )}
            <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', background: 'var(--paper)', borderBottom: '1px solid var(--ink-200)' }}>
                {path.length > top + 1 && (
                  <button type="button" onClick={() => setNavPath(path.slice(0, top))} aria-label={t('Retour à l’écran d’accueil')} title={t('Écran d’accueil')}
                    style={{ width: 34, height: 34, padding: 0, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-700)' }}>
                    <Icon name="store" size={16} />
                  </button>
                )}
                {path.length > top && (
                  <button type="button" onClick={() => setNavPath(path.slice(0, -1))}
                    style={{ height: 34, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)' }}>
                    <Icon name="chevronL" size={14} />{t('Retour')}
                  </button>
                )}
                <span style={{ fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>
                  {levelTile && isSourceTile(levelTile)
                    ? (() => { const n = countOf(levelTile); return n === null ? t('Lecture…') : t('{n} articles', { n }); })()
                    : t('{n} tuiles', { n: drawnCount(levelTiles(layout, level), sellable) })}
                </span>
              </div>
              <div ref={gridRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: D.gridPad, ...grid.style }}>
                {drawn.map((requested, i) => {
                  const { style, height, width: tileWidth, variant } = grid.place(i);
                  // La version qui TIENT dans la tuile (`studioGrid`) — celle que la caisse dessinera à cette largeur.
                  const d = variant === requested.cell.variant ? requested : { ...requested, cell: { ...requested.cell, variant } };
                  const e = d.entry;
                  return e.tile !== null
                    ? <PreviewTile key={e.key} d={d} tile={e.tile} name={nameOf(e.tile)} count={opensLevel(e.tile.kind) ? countOf(e.tile) : null} place={style} height={height} width={tileWidth} onTap={() => tap(e.tile)} />
                    : <PouredCell key={e.key} d={d} place={style} height={height} width={tileWidth} onTap={() => addToCart(e.articleId)} />;
                })}
                {levelRef && levelPour?.status !== 'ready' && (
                  <div style={{ gridColumn: '1 / -1', padding: '40px 16px', textAlign: 'center', fontSize: 'var(--t-body)', color: levelPour?.status === 'error' ? 'var(--rose-fg)' : 'var(--ink-500)' }}>
                    {levelPour?.status === 'error' ? (
                      <>
                        {t('Le contenu de cette tuile ne se lit pas : {motif}', { motif: levelPour.message })}
                        <button type="button" onClick={() => onRetryPour(levelRef)}
                          style={{ display: 'block', margin: '8px auto 0', padding: 0, border: 'none', background: 'transparent', color: 'inherit', textDecoration: 'underline', fontWeight: 700 }}>
                          {t('Réessayer')}
                        </button>
                      </>
                    ) : t('Lecture du catalogue…')}
                  </div>
                )}
                {beyond > 0 && (
                  <div style={{ gridColumn: '1 / -1', padding: '10px 4px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
                    {t('{n} articles de plus ne s’affichent pas — au-delà de {max}.', { n: beyond, max: QUICK_POUR_MAX })}
                  </div>
                )}
                {cells.length === 0 && (!levelRef || levelPour?.status === 'ready') && (
                  <div style={{ gridColumn: '1 / -1', padding: '40px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Rien à afficher ici')}</div>
                    <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Ce niveau ne contient aucune tuile que la caisse puisse vendre.')}</div>
                  </div>
                )}
              </div>
            </div>
            {!pocket && (
            <div style={{ flex: `0 0 ${TICKET_W}px`, background: 'var(--paper)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '13px 14px', borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ticket')}</span>
                {cart.length > 0 && (
                  <button type="button" onClick={() => setCart([])} aria-label={t('Vider le ticket')}
                    style={{ width: 28, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-600)' }}>
                    <Icon name="trash" size={13} stroke={2} />
                  </button>
                )}
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {cart.map((line) => (
                  <div key={line.articleId} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', borderBottom: '1px solid var(--ink-100)' }}>
                    <span style={{ width: 22, height: 22, borderRadius: 'var(--r-xs)', background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--t-meta)', fontWeight: 700 }}>{line.qty}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{line.name}</span>
                    <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatPrice(line.priceCents == null ? null : line.priceCents * line.qty)}
                    </span>
                  </div>
                ))}
                {cart.length === 0 && (
                  // ⚠️ DEUX LIGNES ÉQUILIBRÉES, PAS UN RETOUR FORCÉ : la maquette coupe après « article » par un <br>, qui ne vaut que
                  //    pour le français ; `balance` laisse chaque langue couper où elle tombe juste.
                  <div style={{ padding: '34px 16px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)', lineHeight: 1.55, textWrap: 'balance' }}>
                    {t('Touchez une tuile d’article pour l’ajouter au ticket.')}
                  </div>
                )}
              </div>
              <div style={{ padding: '13px 14px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ flex: 1, fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Total')}</span>
                  <span style={{ fontSize: 'var(--t-h2)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(total)}</span>
                </div>
                {unpriced && (
                  <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Hors articles à prix libre, saisis en caisse.')}</div>
                )}
              </div>
            </div>
            )}
            </div>
            {pocket && (
              // LE TICKET EN BARRE DU BAS : le nombre de pièces, le total — la place que le téléphone a.
              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--paper)', borderTop: '1px solid var(--ink-200)' }}>
                <span style={{ minWidth: 24, height: 24, padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-dense)', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }}>{pieces}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Ticket')}</span>
                  {unpriced && <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('Hors articles à prix libre, saisis en caisse.')}</span>}
                </span>
                {/* 18 px, la taille de la maquette : entre les crans de la charte (16,5 et 19), propre au total du téléphone. */}
                <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(total)}</span>
                {cart.length > 0 && (
                  <button type="button" onClick={() => setCart([])} aria-label={t('Vider le ticket')}
                    style={{ width: 30, height: 30, padding: 0, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-600)' }}>
                    <Icon name="trash" size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Une cellule de l'aperçu : ce que le niveau dessine, l'article qu'elle porte, ce qu'elle montre et sa version. */
interface DrawnCell {
  entry: QuickLevelEntry<StudioTile>;
  article: StudioArticle | undefined;
  look: Pick<StudioTileContent, 'face' | 'framing'> & { color: StudioTile['color'] };
  cell: { w: number; h: number; variant: StudioTileContent['variant'] };
}

/**
 * CE QU'UNE CELLULE DESSINERA EN CAISSE — la règle du contrat (`studioFace`) sur les photos que le studio connaît, et la
 * version qui en découle (`quickTileVariant`, charte). Dans le niveau d'une source, un article prend l'habillage qu'elle
 * donne à ses articles (`fromSource`), sauf ce que sa tuile pose.
 */
function drawnCell(entry: QuickLevelEntry<StudioTile>, articles: Record<string, StudioArticle>, fromSource: QuickTileLook | null, screen: QuickScreenVisuals): DrawnCell {
  const tile = entry.tile;
  const article = tile ? (tile.kind === 'article' ? articles[tile.articleId ?? ''] : undefined) : articles[entry.articleId];
  const look = studioFace(tile ?? undefined, article, fromSource, screen);
  return { entry, article, look, cell: { w: tile?.w ?? 1, h: tile?.h ?? 1, variant: quickTileVariant(tile ?? { kind: 'article' }, look.face) } };
}

/**
 * Une tuile telle que la caisse la dessinera, dans sa version — un dossier ou une source annonce ce qu'on y trouve, un
 * article son prix.
 * - `count` : les articles qu'ouvre une tuile qui ouvre un niveau — `null` tant qu'un contenu n'est pas lu.
 */
function PreviewTile({ d, tile, name, count, place, height, width, onTap }: {
  d: DrawnCell; tile: StudioTile; name: string; count: number | null; place: CSSProperties; height: number; width: number; onTap: () => void;
}) {
  if (tile.kind === 'empty') {
    // Une case vide réserve la place et ne se touche pas.
    return <div aria-hidden style={{ ...place, borderRadius: 'var(--r-sm)', border: '1.5px dashed var(--ink-200)' }} />;
  }
  const tone = tileTone(d.look.color);
  const level = opensLevel(tile.kind);
  const content: StudioTileContent = {
    face: d.look.face, framing: d.look.framing, variant: d.cell.variant, name, tone, tall: tile.h > 1, height, width,
    ...(level ? { count: { n: count } } : { article: d.article ? articleContent(d.article) : undefined }),
  };
  return (
    <button type="button" data-qs-variant={content.variant} onClick={onTap} style={{ ...studioTileFrame(content, level), ...place }}>
      <StudioTileBody {...content} />
    </button>
  );
}

/**
 * UN ARTICLE DÉVERSÉ PAR UNE SOURCE — dessiné comme un article posé : dans l'habillage que sa source lui donne, sinon celui de l'écran.
 * ⚠️ Au-delà des premiers articles d'une très grande source, le serveur n'envoie pas le détail : la cellule le DIT et
 *    ne réagit pas, plutôt que d'ajouter au ticket simulé un article sans nom ni prix.
 */
function PouredCell({ d, place, height, width, onTap }: { d: DrawnCell; place: CSSProperties; height: number; width: number; onTap: () => void }) {
  const a = d.article;
  if (!a) {
    return (
      <div style={{ ...place, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, textAlign: 'center', border: '1px dashed var(--ink-200)', borderRadius: 14, fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>
        {t('Détail non chargé')}
      </div>
    );
  }
  const content: StudioTileContent = {
    face: d.look.face, framing: d.look.framing, variant: d.cell.variant, name: a.name, tone: tileTone(d.look.color), tall: false, height, width, article: articleContent(a),
  };
  return (
    <button type="button" onClick={onTap} style={{ ...studioTileFrame(content, false), ...place }}>
      <StudioTileBody {...content} />
    </button>
  );
}
